import { useState, useEffect, useRef, useCallback } from 'react';

interface TeamMember {
  id: number;
  name: string;
  role: string;
  specialty: string;
  image: string;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  color: string;
}

const teamMembers: TeamMember[] = [
  { id: 1, name: 'Juan Vallejo', role: 'COO', specialty: 'Operaciones', image: '/team/juan-vallejo.png' },
  { id: 2, name: 'Brayan Sinisterra', role: 'CTO', specialty: 'Tecnología', image: '/team/bryanS.png' },
  { id: 3, name: 'Juan Arzayus', role: 'CFO', specialty: 'Finanzas', image: '/team/juan-arzayus.png' },
  { id: 4, name: 'Daniel Largo ', role: 'CCO', specialty: 'Marketing', image: '/team/daniel-largo.png' }
];

export default function Team() {
  const rotationRef = useRef(0);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [renderTick, setRenderTick] = useState(0);
  const isAutoRotatingRef = useRef(true);
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animationRef = useRef<number>(0);
  const autoRotateRef = useRef<number>(0);
  const resumeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const memberCount = teamMembers.length;
  const angleStep = 360 / memberCount;
  const [radius, setRadius] = useState(300);
  const [perspective, setPerspective] = useState(1500);

  // Responsive radius & perspective
  useEffect(() => {
    const updateDimensions = () => {
      const w = window.innerWidth;
      if (w < 480) { setRadius(150); setPerspective(600); }
      else if (w < 640) { setRadius(190); setPerspective(800); }
      else if (w < 768) { setRadius(230); setPerspective(1000); }
      else if (w < 1024) { setRadius(270); setPerspective(1200); }
      else { setRadius(320); setPerspective(1500); }
    };
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Rotación automática con requestAnimationFrame (sin setState por frame)
  useEffect(() => {
    let running = true;
    const animate = () => {
      if (!running) return;
      if (isAutoRotatingRef.current && !isDraggingRef.current) {
        rotationRef.current += 0.15;
        setRenderTick(t => t + 1);
      }
      autoRotateRef.current = requestAnimationFrame(animate);
    };
    autoRotateRef.current = requestAnimationFrame(animate);
    return () => { running = false; cancelAnimationFrame(autoRotateRef.current); };
  }, []);

  // Partículas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = canvas.parentElement?.offsetHeight || 600;
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    const colors = ['#00C2FF', '#1A73E8', '#ffffff'];
    particlesRef.current = Array.from({ length: 40 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.5,
      vy: (Math.random() - 0.5) * 0.5,
      radius: Math.random() * 1.5 + 0.5,
      color: colors[Math.floor(Math.random() * colors.length)]
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particlesRef.current.forEach((p, i) => {
        p.x = (p.x + p.vx + canvas.width) % canvas.width;
        p.y = (p.y + p.vy + canvas.height) % canvas.height;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = 0.3;
        ctx.fill();

        particlesRef.current.slice(i + 1).forEach(p2 => {
          const dist = Math.hypot(p.x - p2.x, p.y - p2.y);
          if (dist < 150) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = p.color;
            ctx.globalAlpha = (1 - dist / 150) * 0.1;
            ctx.stroke();
          }
        });
      });
      animationRef.current = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationRef.current);
    };
  }, []);

  // Handlers de interacción con refs para evitar re-renders
  const handleStart = useCallback((clientX: number) => {
    isDraggingRef.current = true;
    isAutoRotatingRef.current = false;
    startXRef.current = clientX;
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
  }, []);

  const handleMove = useCallback((clientX: number) => {
    if (!isDraggingRef.current) return;
    const deltaX = clientX - startXRef.current;
    rotationRef.current += deltaX * 0.4;
    startXRef.current = clientX;
    setRenderTick(t => t + 1);
  }, []);

  const handleEnd = useCallback(() => {
    isDraggingRef.current = false;
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
    resumeTimeoutRef.current = setTimeout(() => {
      isAutoRotatingRef.current = true;
    }, 3000);
  }, []);

  // Limpiar timeout al desmontar
  useEffect(() => {
    return () => {
      if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
    };
  }, []);

  // Touch handlers con preventDefault para evitar scroll de la página
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    handleStart(e.touches[0].clientX);
  }, [handleStart]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    handleMove(e.touches[0].clientX);
  }, [handleMove]);

  // Calcular índice activo de forma estable
  const normalizedRotation = (((-rotationRef.current % 360) + 360) % 360);
  const activeIndex = Math.round(normalizedRotation / angleStep) % memberCount;

  return (
    <section id="team" className="py-16 sm:py-24 lg:py-32 bg-[#050608] relative overflow-hidden">
      <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none opacity-50" />
      
      {/* Luces de ambiente sutiles */}
      <div className="absolute top-1/2 left-0 -translate-y-1/2 w-64 sm:w-96 h-64 sm:h-96 bg-[#00C2FF]/10 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute top-1/2 right-0 -translate-y-1/2 w-64 sm:w-96 h-64 sm:h-96 bg-[#1A73E8]/10 blur-[120px] rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        <div className="text-center mb-12 sm:mb-18 lg:mb-24">
          <h2 className="text-3xl sm:text-5xl md:text-7xl font-bold text-white tracking-tight">
            Nuestro <span className="text-[#86868b]">Equipo.</span>
          </h2>
        </div>

        <div 
          ref={containerRef}
          className="relative h-[350px] sm:h-[420px] md:h-[500px] flex items-center justify-center cursor-grab active:cursor-grabbing select-none"
          onMouseDown={(e) => { e.preventDefault(); handleStart(e.clientX); }}
          onMouseMove={(e) => handleMove(e.clientX)}
          onMouseUp={handleEnd}
          onMouseLeave={handleEnd}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleEnd}
          style={{ perspective: `${perspective}px`, touchAction: 'none' }}
        >
          <div 
            className="relative w-full h-full flex items-center justify-center"
            style={{ 
              transformStyle: 'preserve-3d', 
              transform: `rotateY(${rotationRef.current}deg)`,
            }}
          >
            {teamMembers.map((member, index) => {
              const cardAngle = index * angleStep;
              
              // Calcular ángulo visible real para determinar si está al frente
              const totalAngle = (rotationRef.current + cardAngle) % 360;
              const normalAngle = ((totalAngle % 360) + 360) % 360;
              const isVisible = normalAngle < 90 || normalAngle > 270;
              const isFront = normalAngle < 45 || normalAngle > 315;

              return (
                <div
                  key={member.id}
                  className="absolute"
                  style={{
                    transformStyle: 'preserve-3d',
                    transform: `rotateY(${cardAngle}deg) translateZ(${radius}px)`,
                    backfaceVisibility: 'hidden',
                  }}
                >
                  <div 
                    className={`w-36 h-52 sm:w-44 sm:h-60 md:w-52 md:h-72 rounded-[1.5rem] sm:rounded-[2rem] md:rounded-[2.5rem] overflow-hidden border bg-[#0d0f14] transition-[border-color,box-shadow,filter,opacity] duration-500 ${
                      isFront 
                        ? 'border-[#00C2FF]/50 shadow-[0_0_40px_rgba(0,194,255,0.2)] scale-105' 
                        : isVisible 
                          ? 'border-white/10 opacity-60' 
                          : 'border-white/5 opacity-0'
                    }`}
                    style={{ backfaceVisibility: 'hidden' }}
                  >
                    <div className="w-full h-3/4 overflow-hidden flex items-center justify-center bg-gradient-to-b from-white/10 to-transparent">
                      {member.image ? (
                        <img 
                          src={member.image} 
                          alt={member.name} 
                          className="w-full h-full object-cover pointer-events-none" 
                          draggable={false}
                        />
                      ) : (
                        <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-full bg-white/10 flex items-center justify-center">
                          <i className="ri-user-line text-4xl sm:text-5xl text-white/40"></i>
                        </div>
                      )}
                    </div>
                    <div className="p-2 sm:p-3 md:p-4 text-center">
                      <h3 className="text-white font-bold text-xs sm:text-sm tracking-tight">{member.name}</h3>
                      <p className="text-[#00C2FF] text-[8px] sm:text-[10px] font-bold uppercase tracking-widest mt-1">{member.role}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Info del miembro activo */}
        <div className="text-center mt-6 sm:mt-8">
          <p className="text-white/40 text-xs sm:text-sm font-medium tracking-widest uppercase">
            {teamMembers[activeIndex]?.specialty}
          </p>
        </div>

        {/* Indicadores de navegación - clickeables */}
        <div className="flex justify-center gap-3 mt-6 sm:mt-8">
          {teamMembers.map((_, i) => (
            <button 
              key={i}
              onClick={() => {
                const targetAngle = -(i * angleStep);
                rotationRef.current = targetAngle;
                isAutoRotatingRef.current = false;
                if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
                resumeTimeoutRef.current = setTimeout(() => {
                  isAutoRotatingRef.current = true;
                }, 3000);
                setRenderTick(t => t + 1);
              }}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                activeIndex === i ? 'w-8 bg-[#00C2FF]' : 'w-2 bg-white/20 hover:bg-white/40'
              }`} 
            />
          ))}
        </div>

        {/* Instrucción de interacción */}
        <p className="text-center text-white/20 text-[10px] sm:text-xs mt-4 tracking-widest uppercase">
          <i className="ri-drag-move-line mr-1"></i>
          Arrastra para explorar
        </p>
      </div>
    </section>
  );
}